import {
  KikGenericLoggerMethodKeys,
  KikLoggerConfig,
  KikLoggerService,
  KikLoggingLevels,
  kikNoop,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import type TransportStream from "winston-transport";

import CommonUtils from "~/utils/common-utils";

type LoggerConfig = KikLoggerConfig & {
  customTags: Record<string, unknown>;
};

export default class Logger extends KikLoggerService {
  /**
   * Exposed here to allow declaring a new Logger instance, but maintaining the global app level
   */
  static get appLogLevel() {
    return (
      KikLoggingLevels[KikLoggingLevels[+process.env.NEXT_PUBLIC_LOG_LEVEL!] as keyof typeof KikLoggingLevels] ||
      KikLoggingLevels.OFF
    );
  }

  /**
   * This method exists to maintain retro-compatibility,
   * since the new version of the base logger uses a different naming convention
   * where static readonly getters are UPPER_CASE
   */
  static get instance() {
    return this.INSTANCE;
  }

  protected static _enableDatadog: boolean = CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_DATADOG_ENABLED);

  /**
   * Useful to read tags from the outside (e.g. merge existing tags with new tags)
   */
  get customTags(): LoggerConfig["customTags"] {
    return this._customTags;
  }

  protected _customTags: LoggerConfig["customTags"] = {};

  //

  static init(config: Partial<LoggerConfig>, appName?: string) {
    const instance: Logger = super.init(config, appName) as Logger;
    instance._customTags = config.customTags || instance._customTags;

    return instance;
  }

  //

  preprocessArgs(level: KikGenericLoggerMethodKeys, ...args: any[]): any[] {
    const now = new Date();
    const customTags = Object.entries(this._customTags || {})
      .filter((tag) => Boolean(tag[1]))
      .map((tag) => tag.join(":"));

    return [
      `${now.toISOString()} - ${args.shift()}`.trim(),
      ...args.map((value) => {
        return {
          tags: [this._appName, level, ...customTags],
          timestamp: +now,
          sessionId: this.sessionId,
          metadata: this.filterSensitiveData(value),
        };
      }),
    ];
  }

  /**
   * @override
   * @param newValue
   * @private
   */
  protected async _updateLevel(newValue: KikLoggingLevels = KikLoggingLevels.WARN) {
    const staticSelf = this.constructor as typeof Logger;
    staticSelf.describe().forEach((key) => {
      this[key] = kikNoop;
    });
    Logger._level = newValue;

    let logger: Parameters<typeof this._setup>[0] = console;

    if (typeof window !== "undefined") {
      // Client-side logs
      if (staticSelf._enableDatadog) {
        logger = await import("@datadog/browser-logs").then((lib) => lib.datadogLogs);
      }
    } else {
      // Server-side logs
      const winston = await import("winston");
      const packageJson = await import("../../../package.json");

      let transports: TransportStream[] = [new winston.transports.Console({ level: "debug" })];

      if (staticSelf._enableDatadog) {
        const params = new URLSearchParams({
          "dd-api-key": process.env.DATADOG_API_KEY!,
          ddsource: "serverless",
          service: process.env.NEXT_PUBLIC_DATADOG_SERVICE_BACKEND!,
          ddtags: `env:${process.env.NEXT_PUBLIC_DATADOG_ENV},version:${packageJson.version}`,
        });

        transports.push(
          new winston.transports.Http({
            host: "http-intake.logs.datadoghq.eu",
            path: `/api/v2/logs?${params}`,
            ssl: true,
          })
        );
      }

      logger = winston.createLogger({
        level: "debug",
        exitOnError: true,
        format: winston.format.json(),
        transports,
      });
    }

    console.info(`Logger: update level to ${newValue}, using ${staticSelf._enableDatadog ? "datadog" : "console"}`);
    this._setup(logger);
  }
}

Logger.init({
  level: Logger.appLogLevel,
  enableSessionId: !0,
  enablePreprocessing: !0,
});
